import { useQuery } from '@tanstack/react-query';
import { fetchWithSchema } from '@/lib/api';
import { getSchemaScopedQueryKey } from '@/lib/schemaStorage';

interface AppConfiguration {
  appointmentType: 'clinic_hours' | 'lodging_days' | 'website_presentation';
  appointmentMode?: string;
  enableAppointments?: boolean;
  enableServiceBooking?: boolean;
  enableEmployeeBooking?: boolean;
  enableServiceReservation?: boolean;
}

/**
 * Hook to fetch and cache app configuration.
 * Uses React Query to cache the response and prevent unnecessary refetches.
 */
export function useAppConfiguration() {
  const { data, isLoading, error } = useQuery<AppConfiguration>({
    queryKey: getSchemaScopedQueryKey(['app-configuration']),
    queryFn: async () => {
      const response = await fetchWithSchema('/api/appconfiguration');
      if (!response.ok) {
        throw new Error(`Failed to fetch app configuration: ${response.status}`);
      }
      const config = await response.json();
      return config;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - config doesn't change often (increased from 5)
    gcTime: 1000 * 60 * 60, // 1 hour - keep in cache longer (increased from 30 minutes)
    retry: 1, // Retry only once on failure (reduced from 2 for faster failure)
    retryDelay: 1000, // Fixed 1s delay (faster than exponential backoff)
    // Default value to prevent flickering
    placeholderData: { appointmentType: 'clinic_hours' },
    // Prevent duplicate requests - React Query will deduplicate automatically
    refetchOnMount: false, // Don't refetch if data exists in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  return {
    appConfig: data || { appointmentType: 'clinic_hours' },
    isLoading,
    error,
  };
}

